import React, { memo } from "react";
import { BsDot } from "react-icons/bs";
import { DateTime } from "luxon";
import styles from "./tender.module.scss";
import { capitalizeFirstLetter } from "../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection";

// Approximation: EDI provides only state (no tz/latlng), so we default to a timezone per state.
// Multi-timezone states will sometimes be wrong; tweak defaults if needed for your operation.
const STATE_TO_TIMEZONE = {
    AL: "America/Chicago",
    AK: "America/Anchorage",
    AZ: "America/Phoenix",
    AR: "America/Chicago",
    CA: "America/Los_Angeles",
    CO: "America/Denver",
    CT: "America/New_York",
    DE: "America/New_York",
    FL: "America/New_York",
    GA: "America/New_York",
    HI: "Pacific/Honolulu",
    IA: "America/Chicago",
    ID: "America/Denver",
    IL: "America/Chicago",
    IN: "America/Indiana/Indianapolis",
    KS: "America/Chicago",
    KY: "America/New_York",
    LA: "America/Chicago",
    MA: "America/New_York",
    MD: "America/New_York",
    ME: "America/New_York",
    MI: "America/Detroit",
    MN: "America/Chicago",
    MO: "America/Chicago",
    MS: "America/Chicago",
    MT: "America/Denver",
    NC: "America/New_York",
    ND: "America/Chicago",
    NE: "America/Chicago",
    NH: "America/New_York",
    NJ: "America/New_York",
    NM: "America/Denver",
    NV: "America/Los_Angeles",
    NY: "America/New_York",
    OH: "America/New_York",
    OK: "America/Chicago",
    OR: "America/Los_Angeles",
    PA: "America/New_York",
    RI: "America/New_York",
    SC: "America/New_York",
    SD: "America/Chicago",
    TN: "America/Chicago",
    TX: "America/Chicago",
    UT: "America/Denver",
    VA: "America/New_York",
    VT: "America/New_York",
    WA: "America/Los_Angeles",
    WI: "America/Chicago",
    WV: "America/New_York",
    WY: "America/Denver",
    DC: "America/New_York",
};

export function getDefaultTimezone() {
    try {
        return localStorage.getItem("defaultTimezone") || "America/Chicago";
    } catch (e) {
        return "America/Chicago";
    }
}

export function getTimezoneFromState(state) {
    if (!state || typeof state !== "string") return null;
    const key = state.trim().toUpperCase();
    return STATE_TO_TIMEZONE[key] || null;
}

export function normalizeSingleStop(stop) {
    try {
        if (!stop || typeof stop !== "object") return null;

        const refNumbers = Array.isArray(stop?.reference_numbers)
            ? stop.reference_numbers
                .map((r) => {
                    const t = r?.type ?? null;
                    const v = r?.value ?? null;
                    if (!t || !v) return null;
                    return `${t}-${v}`;
                })
                .filter(Boolean)
            : [];

        const stopNotes = Array.isArray(stop?.notes)
            ? stop.notes
                .map((n) => (typeof n?.content === "string" ? n.content : null))
                .filter(Boolean)
            : [];

        const city = stop?.location?.address?.city ?? null;
        const state = stop?.location?.address?.state ?? null;
        const zip = stop?.location?.address?.postal_code ?? null;
        const cityStateZip = city && state && zip ? `${city} ${state} ${zip}` : null;

        // Convert UTC ISO to "local" based on the stop's state (approx).
        let apptTime = null;
        const startIso = stop?.scheduled_at?.start_datetime ?? null;
        if (typeof startIso === "string" && startIso) {
            const zone =  getDefaultTimezone(); //getTimezoneFromState(state) ||
            apptTime = isoToString(startIso, zone);
        }

        return {
            type: stop?.reason ?? "-",
            stopNumber: stop?.sequence_number ?? "-",
            refs: refNumbers,
            notes: stopNotes,
            apptTime,
            companyName: stop?.location?.name ?? null,
            line1: stop?.location?.address?.line1 ?? null,
            line2: stop?.location?.address?.line2 ?? null,
            cityStateZip,
        };
    } catch (e) {
        return null;
    }
}
export function isoToString(startIso, zone){
    const dt = DateTime.fromISO(startIso, { setZone: true }).setZone(zone).setLocale("en-US");
    return dt.isValid ? `${dt.toFormat("MM/dd HH:mm")} ${dt.offsetNameShort}` : null;
}
export const ExpendedTenderDetails = memo(({ DataEntity }) => {
    const stops = DataEntity?.data?.stops ?? [];
    if (!stops.length) return null;

    return (
        <div className={styles.tenderDetails}>
            {stops.map((stop) => {
                const normalizedStop = normalizeSingleStop(stop);
                if (!normalizedStop) return null;
                return (
                    <div key={`stop-${normalizedStop.stopNumber}`} className={styles.stop}>
                        <div className={`${styles.section} ${styles.xtrasmallSection}`}>
                            <div className={styles.sectionLabel}>
                                Stop# <span className={styles.value}>{normalizedStop.stopNumber}</span>
                            </div>
                            <div className={styles.sectionLabel}>
                                Type <span className={styles.value}>{capitalizeFirstLetter(normalizedStop.type)}</span>
                            </div>
                        </div>
                        <div className={`${styles.section} ${styles.smallSection}`}>
                            <div className={styles.sectionLabel}>Reference Numbers</div>
                            <div className={styles.notes}>
                                {normalizedStop.refs.length > 0
                                    ? normalizedStop.refs.map((refNumber) => (
                                          <div key={refNumber}>
                                              <BsDot /> {refNumber}
                                          </div>
                                      ))
                                    : <div className={styles.noNotes}>—</div>}
                            </div>
                        </div>
                        <div className={`${styles.section} ${styles.smallSection}`}>
                            <div className={styles.sectionLabel}>Stop Notes</div>
                            <div className={styles.notes}>
                                {normalizedStop.notes.length > 0
                                    ? normalizedStop.notes.map((note, idx) => (
                                          <div key={idx}>
                                              <BsDot /> {note}
                                          </div>
                                      ))
                                    : <div className={styles.noNotes}>—</div>}
                            </div>
                        </div>
                        <div className={`${styles.section} ${styles.smallSection}`}>
                            <div className={styles.sectionLabel}>Appointment</div>
                            <div className={styles.value}>{normalizedStop.apptTime ?? "—"}</div>
                        </div>
                        <div className={`${styles.section} ${styles.largeSection}`}>
                            <div className={styles.sectionLabel}>Address</div>
                            {[normalizedStop.companyName, normalizedStop.line1, normalizedStop.line2, normalizedStop.cityStateZip]
                                .filter(Boolean)
                                .map((line, idx) => (
                                    <div key={`addr-${idx}`} className={styles.value}>{line}</div>
                                ))}
                            {![normalizedStop.companyName, normalizedStop.line1, normalizedStop.line2, normalizedStop.cityStateZip].some(Boolean) && (
                                <div className={styles.value}>—</div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
});